import type { FunctionComponent } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { ViewModelStreamConfig } from './types';
import { combineLatest, map, shareReplay, withLatestFrom } from 'rxjs';
import { partitionByKeys } from '@ardoq/rxbeach';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { connectRelationshipsRenderingDataLayer } from './connectRelationshipsRenderingDataLayer';
import { getViewpointModeSourceData$ } from './viewModel$/getViewpointModeSourceData$';
import {
  COLLAPSED_GROUP_IDS_PARTITION_KEYS,
  RENDERING_DATA_PARTITION_KEYS,
  SOURCE_DATA_PARTITION_KEYS,
  UI_PROPS_PARTITION_KEYS,
} from './viewModel$/viewSettingsPartitions';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

const getViewModelStreamConfig = (): ViewModelStreamConfig => {
  const currentSettings$ = getViewpointModeViewSettingsWithChanges$(
    VIEW_ID,
    VIEW_INSTANCE_ID
  ).pipe(
    map(({ currentSettings }) => currentSettings),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  const [
    sourceDataSettings$,
    collapsedGroupIdsSettings$,
    renderingDataSettings$,
    uiPropsSettings$,
  ] = partitionByKeys(currentSettings$, [
    SOURCE_DATA_PARTITION_KEYS,
    COLLAPSED_GROUP_IDS_PARTITION_KEYS,
    RENDERING_DATA_PARTITION_KEYS,
    UI_PROPS_PARTITION_KEYS,
  ]);
  return {
    viewInstanceId: VIEW_INSTANCE_ID,
    viewSettingsForModelSourceData$: sourceDataSettings$.pipe(
      withLatestFrom(collapsedGroupIdsSettings$),
      map(([source, collapsedGroupIds]) => ({
        ...source,
        ...collapsedGroupIds,
      }))
    ),
    viewSettingsForRenderingData$: combineLatest({
      collapsedGroupIds: collapsedGroupIdsSettings$,
      rendering: renderingDataSettings$,
    }).pipe(
      map(({ collapsedGroupIds, rendering }) => ({
        ...collapsedGroupIds,
        ...rendering,
      }))
    ),
    uiPropsSettings$,
  };
};

export const ViewpointModeRelationshipsView: FunctionComponent =
  connectRelationshipsRenderingDataLayer({
    getViewModelSourceData$: getViewpointModeSourceData$,
    viewModelStreamConfig: getViewModelStreamConfig(),
    isViewpointMode: true,
  });
